/**
 * Growth Intelligence V2 — Premium coach view.
 *
 * UI-only rewrite. Consumes the same `/growth/dashboard` payload the backend
 * has always returned. Nothing is invented — sections render `null` when the
 * corresponding signal is missing.
 *
 * Sections (in reading order):
 *   1. HERO — headline + dynamic subtitle derived from overview.
 *   2. SCORE GLOBAL — big animated ring, avg_ai_score.
 *   3. TOP PRIORITY — one high-impact next action.
 *   4. STRENGTHS / WEAKNESSES — two 3-line cards.
 *   5. AI STRATEGY — the GPT strategy.recommendations as compact cards.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  RefreshCw, ArrowLeft, Sparkles, TrendingUp, Wand2, Target,
  Zap, CheckCircle2, AlertCircle, ArrowRight,
} from 'lucide-react';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import AnimatedNumber from '@/components/premium/AnimatedNumber';
import EmptyState from '@/components/premium/EmptyState';
import { GrowthIllustration } from '@/components/premium/illustrations';
import { SkeletonCard } from '@/components/premium/Skeleton';

const ACC = '#D4FF00';

/* ─────────────── Helpers ─────────────── */

const platformLabel = (p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : null);

const buildHeroSubtitle = (o = {}, insights = [], lang = 'fr') => {
  const {
    clips_published = 0,
    avg_hook_score = null,
    avg_ai_score = 0,
    estimated_total_views = 0,
    avg_clip_duration = null,
  } = o;
  if ((o.clips_generated || 0) === 0) {
    return lang === 'fr'
      ? 'Upload ta première vidéo pour lancer l\'analyse IA.'
      : 'Upload your first video to start the AI analysis.';
  }
  if (avg_hook_score !== null && avg_hook_score < 70) {
    return lang === 'fr'
      ? 'Ta rétention est ton principal levier — travaille les 3 premières secondes.'
      : 'Retention is your biggest lever — polish the first 3 seconds.';
  }
  if (estimated_total_views >= 50000) {
    return lang === 'fr'
      ? `Cette semaine tu peux dépasser ${new Intl.NumberFormat('fr-FR').format(Math.round(estimated_total_views * 1.5))} vues.`
      : `You can pass ${new Intl.NumberFormat('en-US').format(Math.round(estimated_total_views * 1.5))} views this week.`;
  }
  if (avg_ai_score >= 85) {
    return lang === 'fr'
      ? 'Excellent — tes clips sont dans le top 20 % des benchmarks.'
      : "Excellent — your clips sit in the top 20% of benchmarks.";
  }
  if (clips_published < 3) {
    return lang === 'fr'
      ? 'Publie davantage pour laisser l\'IA apprendre ton style.'
      : 'Publish more so the AI can learn your voice.';
  }
  if (avg_clip_duration && avg_clip_duration < 18) {
    return lang === 'fr'
      ? 'Allonge tes clips à 20–30 s pour rester dans le sweet spot.'
      : 'Extend clips to 20–30 s to hit the sweet spot.';
  }
  if (insights[0]?.title) {
    return insights[0].title;
  }
  return lang === 'fr'
    ? 'Ton hook progresse — continue à publier.'
    : 'Your hook is trending up — keep publishing.';
};

const scoreVerdict = (score, lang) => {
  if (score >= 85) return { label: lang === 'fr' ? 'Excellent' : 'Excellent', tone: 'strong' };
  if (score >= 70) return { label: lang === 'fr' ? 'Bon' : 'Good', tone: 'okay' };
  return { label: lang === 'fr' ? 'À améliorer' : 'Needs work', tone: 'weak' };
};

const derivePriority = (o = {}, insights = [], lang = 'fr') => {
  const {
    avg_hook_score = null, clips_published = 0,
    avg_clip_duration = null, avg_ai_score = 0,
  } = o;
  if (avg_hook_score !== null && avg_hook_score < 70) {
    return {
      title: lang === 'fr' ? 'Améliore ton hook' : 'Sharpen your hook',
      reason: lang === 'fr'
        ? `Ton hook moyen est de ${avg_hook_score}/100. Vise 3 secondes plus percutantes pour retenir l'audience.`
        : `Your average hook score is ${avg_hook_score}/100. Aim for tighter, punchier openings.`,
      cta: { to: '/upload', label: lang === 'fr' ? 'Uploader et refaire' : 'Upload and refine' },
      testid: 'priority-hook',
    };
  }
  if (clips_published < 3) {
    return {
      title: lang === 'fr' ? 'Publie davantage' : 'Publish more',
      reason: lang === 'fr'
        ? `Seulement ${clips_published} publication cette semaine. La régularité est le premier signal d'algorithme.`
        : `Only ${clips_published} publication${clips_published === 1 ? '' : 's'} this week. Consistency is the #1 algorithm signal.`,
      cta: { to: '/publishing', label: lang === 'fr' ? 'Ouvrir Publishing' : 'Open Publishing' },
      testid: 'priority-publish',
    };
  }
  if (avg_clip_duration && avg_clip_duration < 18) {
    return {
      title: lang === 'fr' ? 'Allonge tes clips' : 'Extend your clips',
      reason: lang === 'fr'
        ? `Tes clips durent ${Math.round(avg_clip_duration)}s en moyenne — vise 20–30s pour maximiser la rétention.`
        : `Your clips average ${Math.round(avg_clip_duration)}s — aim for 20–30s for max retention.`,
      cta: { to: '/dashboard', label: lang === 'fr' ? 'Voir mes clips' : 'See my clips' },
      testid: 'priority-duration',
    };
  }
  if (avg_ai_score < 70) {
    return {
      title: lang === 'fr' ? 'Travaille la qualité' : 'Improve quality',
      reason: lang === 'fr'
        ? `Ton score moyen est de ${Math.round(avg_ai_score)}/100. Génère plus de variantes pour laisser l'IA sélectionner tes meilleurs moments.`
        : `Your average score is ${Math.round(avg_ai_score)}/100. Generate more variants to let the AI pick the best moments.`,
      cta: { to: '/upload', label: lang === 'fr' ? 'Nouvelle vidéo' : 'New video' },
      testid: 'priority-quality',
    };
  }
  if (insights[0]?.title) {
    return {
      title: insights[0].title,
      reason: insights[0].explanation,
      cta: { to: '/publishing', label: lang === 'fr' ? 'Passer à l\'action' : 'Take action' },
      testid: 'priority-insight',
    };
  }
  return {
    title: lang === 'fr' ? 'Continue sur ta lancée' : 'Keep the momentum',
    reason: lang === 'fr'
      ? 'Tes signaux sont bons. Continue à publier régulièrement pour capitaliser sur ta traction.'
      : 'Your signals look good. Keep publishing consistently to capitalise on traction.',
    cta: { to: '/publishing', label: lang === 'fr' ? 'Programmer un post' : 'Schedule a post' },
    testid: 'priority-momentum',
  };
};

const deriveStrengthsWeaknesses = (o = {}, insights = [], lang = 'fr') => {
  const strengths = [];
  const weaknesses = [];

  if (o.avg_ai_score >= 75) {
    strengths.push(lang === 'fr'
      ? `Score IA moyen ${Math.round(o.avg_ai_score)}/100 — bien au-dessus du benchmark.`
      : `Avg AI score ${Math.round(o.avg_ai_score)}/100 — above benchmark.`);
  }
  if (o.best_ai_score >= 90) {
    strengths.push(lang === 'fr'
      ? `Meilleur clip ${o.best_ai_score}/100 — potentiel viral confirmé.`
      : `Best clip ${o.best_ai_score}/100 — viral potential confirmed.`);
  }
  if (o.best_platform && (o.best_platform_count || 0) > 0) {
    strengths.push(lang === 'fr'
      ? `${platformLabel(o.best_platform)} est ta plateforme la plus active.`
      : `${platformLabel(o.best_platform)} is your top platform.`);
  }
  if (insights.some((i) => i.confidence === 'high' || i.confidence === 'medium')) {
    const good = insights.find((i) => i.confidence === 'high') || insights[0];
    if (good?.title) strengths.push(good.title);
  }

  if (o.avg_hook_score !== null && o.avg_hook_score < 70) {
    weaknesses.push(lang === 'fr'
      ? `Hook moyen ${o.avg_hook_score}/100 — sous le seuil de 70.`
      : `Avg hook ${o.avg_hook_score}/100 — below the 70 threshold.`);
  }
  if ((o.clips_published || 0) < 3) {
    weaknesses.push(lang === 'fr'
      ? `Seulement ${o.clips_published || 0} publication${(o.clips_published || 0) > 1 ? 's' : ''} cette semaine.`
      : `Only ${o.clips_published || 0} publication${(o.clips_published || 0) === 1 ? '' : 's'} this week.`);
  }
  if (o.avg_clip_duration && o.avg_clip_duration < 18) {
    weaknesses.push(lang === 'fr'
      ? `Durée moyenne ${Math.round(o.avg_clip_duration)}s — hors sweet spot 20–30s.`
      : `Avg duration ${Math.round(o.avg_clip_duration)}s — outside the 20–30s sweet spot.`);
  }
  if ((o.estimated_total_views || 0) < 1000 && (o.clips_generated || 0) > 3) {
    weaknesses.push(lang === 'fr'
      ? 'Reach faible malgré des clips générés — publie plus régulièrement.'
      : 'Reach is low despite generated clips — publish more often.');
  }

  return {
    strengths: strengths.slice(0, 3),
    weaknesses: weaknesses.slice(0, 3),
  };
};

/* ─────────────── Score Ring ─────────────── */
const ScoreRing = ({ score = 0 }) => {
  const size = 176;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const dash = (clamped / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#ffffff14" strokeWidth={stroke} fill="none" />
      <motion.circle
        cx={size / 2} cy={size / 2} r={r}
        stroke={ACC} strokeWidth={stroke} fill="none" strokeLinecap="round"
        strokeDasharray={`${dash} ${c}`}
        initial={{ strokeDasharray: `0 ${c}` }}
        animate={{ strokeDasharray: `${dash} ${c}` }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

/* ─────────────── Component ─────────────── */
const GrowthDashboard = () => {
  const { lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/growth/dashboard').then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const o = data?.overview || {};
  const insights = data?.insights || [];
  const strategy = data?.strategy || { recommendations: [], source: 'fallback' };

  const heroSubtitle = useMemo(() => buildHeroSubtitle(o, insights, lang), [o, insights, lang]);
  const priority = useMemo(() => derivePriority(o, insights, lang), [o, insights, lang]);
  const sw = useMemo(() => deriveStrengthsWeaknesses(o, insights, lang), [o, insights, lang]);

  const score = typeof o.avg_ai_score === 'number' ? o.avg_ai_score : null;
  const verdict = score !== null ? scoreVerdict(score, lang) : null;
  const noData = !loading && data && (o.clips_generated || 0) === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-7xl mx-auto px-6 lg:px-10 py-10"
      data-testid="growth-page"
    >
      {/* Back + refresh */}
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/dashboard"
          className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono flex items-center gap-2 hover:text-zinc-200 transition"
          data-testid="growth-back"
        >
          <ArrowLeft className="w-3 h-3" /> {lang === 'fr' ? 'Dashboard' : 'Dashboard'}
        </Link>
        <button
          onClick={load} disabled={loading}
          data-testid="growth-refresh"
          className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] disabled:opacity-50 text-[11px] uppercase tracking-wider font-mono flex items-center gap-2 transition"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {lang === 'fr' ? 'Rafraîchir' : 'Refresh'}
        </button>
      </div>

      {/* 1 · HERO */}
      <section className="mb-10" data-testid="growth-hero">
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4FF00] font-mono">
          {lang === 'fr' ? 'Coach IA' : 'AI coach'}
        </span>
        <h1 className="font-heading text-5xl lg:text-6xl font-black mt-3 tracking-tighter">
          Growth Intelligence
        </h1>
        <p
          className="text-zinc-300 mt-5 text-lg max-w-2xl leading-relaxed"
          data-testid="growth-hero-subtitle"
        >
          {heroSubtitle}
        </p>
      </section>

      {error && (
        <div className="p-4 mb-6 rounded-xl border border-red-500/30 bg-red-500/5 text-red-300 text-sm font-mono" data-testid="growth-error">
          {error}
        </div>
      )}

      {/* Loading skeletons */}
      {loading && !data && (
        <div className="space-y-6" data-testid="growth-loading">
          <SkeletonCard lines={4} />
          <SkeletonCard lines={5} />
          <div className="grid md:grid-cols-2 gap-4">
            <SkeletonCard lines={3} />
            <SkeletonCard lines={3} />
          </div>
        </div>
      )}

      {/* Empty state — no clips yet */}
      {noData && (
        <EmptyState
          testid="growth-empty"
          illustration={<GrowthIllustration className="w-52 h-40" />}
          title={lang === 'fr' ? "L'IA a besoin de matière" : 'The AI needs more data'}
          description={lang === 'fr'
            ? 'Génère quelques clips pour que la Growth Intelligence commence à identifier tes patterns gagnants.'
            : 'Generate a few clips so Growth Intelligence can start spotting your winning patterns.'}
          ctaLabel={lang === 'fr' ? 'Uploader une vidéo' : 'Upload a video'}
          to="/upload"
        />
      )}

      {!loading && data && !noData && (
        <>
          {/* 2 · SCORE GLOBAL + 3 · TOP PRIORITY (top row) */}
          <div className="grid lg:grid-cols-12 gap-6 mb-10">
            {/* Score global */}
            <div
              className="lg:col-span-5 rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.03] to-transparent p-6 flex items-center gap-6"
              data-testid="growth-score-card"
            >
              <ScoreRing score={score ?? 0} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-2">
                  {lang === 'fr' ? 'Score IA' : 'AI score'}
                </p>
                <div className="flex items-baseline gap-2">
                  <AnimatedNumber
                    value={score ?? 0}
                    className="font-heading text-6xl font-black tracking-tighter leading-none"
                  />
                  <span className="text-zinc-500 text-lg font-mono">/100</span>
                </div>
                {verdict && (
                  <p
                    className={`mt-3 text-sm font-semibold ${
                      verdict.tone === 'strong' ? 'text-[#D4FF00]'
                      : verdict.tone === 'okay' ? 'text-zinc-200'
                      : 'text-amber-400'
                    }`}
                    data-testid="growth-score-verdict"
                  >
                    {verdict.label}
                  </p>
                )}
              </div>
            </div>

            {/* Priority (huge card) */}
            <div
              className="lg:col-span-7 rounded-2xl border border-[#D4FF00]/25 bg-gradient-to-br from-[#D4FF00]/[0.06] to-transparent p-7 flex flex-col justify-between"
              data-testid="growth-priority"
            >
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-md bg-[#D4FF00] text-black flex items-center justify-center">
                    <Zap className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4FF00] font-mono">
                    {lang === 'fr' ? "Priorité aujourd'hui" : 'Today\'s priority'}
                  </span>
                </div>
                <h2
                  className="font-heading text-2xl lg:text-3xl font-black tracking-tight leading-tight mb-3"
                  data-testid={priority.testid}
                >
                  {priority.title}
                </h2>
                <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 max-w-xl">
                  {priority.reason}
                </p>
              </div>
              <div className="mt-6">
                <Link
                  to={priority.cta.to}
                  className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-[#D4FF00] text-black font-semibold text-sm hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4FF00]/25 transition-all"
                  data-testid="growth-priority-cta"
                >
                  {priority.cta.label}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* 4 · STRENGTHS / WEAKNESSES */}
          {(sw.strengths.length > 0 || sw.weaknesses.length > 0) && (
            <section className="grid md:grid-cols-2 gap-4 mb-10" data-testid="growth-sw">
              {sw.strengths.length > 0 && (
                <div
                  className="rounded-2xl border border-white/8 bg-white/[0.02] p-5"
                  data-testid="growth-strengths"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-4 h-4 text-[#D4FF00]" />
                    <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-mono">
                      {lang === 'fr' ? 'Points forts' : 'Strengths'}
                    </h3>
                  </div>
                  <ul className="space-y-2.5">
                    {sw.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-200" data-testid={`growth-strength-${i}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00] mt-2 shrink-0" />
                        <span className="leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {sw.weaknesses.length > 0 && (
                <div
                  className="rounded-2xl border border-white/8 bg-white/[0.02] p-5"
                  data-testid="growth-weaknesses"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-mono">
                      {lang === 'fr' ? 'À améliorer' : 'To improve'}
                    </h3>
                  </div>
                  <ul className="space-y-2.5">
                    {sw.weaknesses.map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-200" data-testid={`growth-weakness-${i}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                        <span className="leading-relaxed">{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* 5 · AI STRATEGY — as premium cards */}
          {strategy.recommendations.length > 0 && (
            <section className="mb-10" data-testid="section-strategy">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-mono">
                    {lang === 'fr' ? 'Stratégie semaine prochaine' : 'Next week strategy'}
                  </span>
                  <h2 className="font-heading text-2xl font-bold mt-1 flex items-center gap-2 tracking-tight">
                    <Sparkles className="w-4 h-4 text-[#D4FF00]" />
                    {lang === 'fr' ? 'Ton plan d\'action' : 'Your action plan'}
                  </h2>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                  {strategy.source === 'gpt' ? 'GPT-5.2' : strategy.source === 'cache' ? 'cached' : 'auto'}
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {strategy.recommendations.map((r, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="p-4 rounded-xl border border-white/8 bg-white/[0.02] hover:border-white/20 transition flex items-start gap-3"
                    data-testid={`strategy-rec-${idx}`}
                  >
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/25 text-[#D4FF00] text-xs font-mono font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <p className="text-sm text-zinc-200 leading-relaxed">{r}</p>
                  </motion.div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </motion.div>
  );
};

export default GrowthDashboard;
